import React from 'react';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DownloadIcon } from 'lucide-react';
import { balanceSheetData } from '../utils/mockData';
const BalanceSheet = () => {
  // Format currency
  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString()}`;
  };
  // Colors for pie charts
  const ASSET_COLORS = ['#3B82F6', '#60A5FA', '#93C5FD', '#6366F1', '#818CF8'];
  const LIABILITY_COLORS = ['#F97316', '#FB923C', '#FDBA74'];
  const EQUITY_COLORS = ['#10B981', '#34D399'];
  // Calculate totals
  const totalAssets = balanceSheetData.assets.reduce((sum, item) => sum + item.value, 0);
  const totalLiabilities = balanceSheetData.liabilities.reduce((sum, item) => sum + item.value, 0);
  const totalEquity = balanceSheetData.equity.reduce((sum, item) => sum + item.value, 0);
  return <div data-id="element-789">
      <div className="mb-6 flex justify-between items-start" data-id="element-790">
        <div data-id="element-791">
          <h1 className="text-2xl font-bold text-gray-800" data-id="element-792">Balance General</h1>
          <p className="text-gray-600" data-id="element-793">
            Análisis detallado de activos, pasivos y capital
          </p>
        </div>
        <button className="flex items-center space-x-2 text-sm bg-blue-600 text-white px-4 py-2.5 rounded-md hover:bg-blue-700 transition-colors" data-id="element-794">
          <DownloadIcon size={18} data-id="element-795" />
          <span data-id="element-796">Exportar PDF</span>
        </button>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-id="element-797">
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 shadow-sm" data-id="element-798">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-799">Total Activos</h3>
          <p className="text-2xl font-bold mt-1 text-blue-700" data-id="element-800">
            {formatCurrency(totalAssets)}
          </p>
        </div>
        <div className="bg-orange-50 p-4 rounded-xl border border-orange-200 shadow-sm" data-id="element-801">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-802">Total Pasivos</h3>
          <p className="text-2xl font-bold mt-1 text-orange-700" data-id="element-803">
            {formatCurrency(totalLiabilities)}
          </p>
        </div>
        <div className="bg-green-50 p-4 rounded-xl border border-green-200 shadow-sm" data-id="element-804">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-805">
            Capital Contable
          </h3>
          <p className="text-2xl font-bold mt-1 text-green-700" data-id="element-806">
            {formatCurrency(totalEquity)}
          </p>
        </div>
      </div>
      {/* Balance Sheet Tables and Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-807">
        {/* Assets Section */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-808">
          <h2 className="text-lg font-semibold mb-4" data-id="element-809">Activos</h2>
          <div className="mb-4" data-id="element-810">
            <div className="overflow-x-auto" data-id="element-811">
              <table className="w-full" data-id="element-812">
                <thead data-id="element-813">
                  <tr className="bg-gray-50" data-id="element-814">
                    <th className="py-2 px-4 text-left text-sm font-medium text-gray-500" data-id="element-815">
                      Concepto
                    </th>
                    <th className="py-2 px-4 text-right text-sm font-medium text-gray-500" data-id="element-816">
                      Valor
                    </th>
                    <th className="py-2 px-4 text-right text-sm font-medium text-gray-500" data-id="element-817">
                      %
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-818">
                  {balanceSheetData.assets.map(item => {
                  const percentage = item.value / totalAssets * 100;
                  return <tr key={item.name} data-id="element-819">
                        <td className="py-2 px-4 text-sm" data-id="element-820">{item.name}</td>
                        <td className="py-2 px-4 text-right text-sm" data-id="element-821">
                          {formatCurrency(item.value)}
                        </td>
                        <td className="py-2 px-4 text-right text-sm" data-id="element-822">
                          {percentage.toFixed(1)}%
                        </td>
                      </tr>;
                })}
                  <tr className="bg-blue-50 font-medium" data-id="element-823">
                    <td className="py-2 px-4 text-sm" data-id="element-824">Total Activos</td>
                    <td className="py-2 px-4 text-right text-sm" data-id="element-825">
                      {formatCurrency(totalAssets)}
                    </td>
                    <td className="py-2 px-4 text-right text-sm" data-id="element-826">100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="h-64" data-id="element-827">
            <ResponsiveContainer width="100%" height="100%" data-id="element-828">
              <PieChart data-id="element-829">
                <Pie data={balanceSheetData.assets} cx="50%" cy="50%" labelLine={false} label={({
                name,
                percent
              }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={80} fill="#8884d8" dataKey="value" data-id="element-830">
                  {balanceSheetData.assets.map((entry, index) => <Cell key={`cell-${index}`} fill={ASSET_COLORS[index % ASSET_COLORS.length]} data-id="element-831" />)}
                </Pie>
                <Tooltip formatter={value => [formatCurrency(value as number), undefined]} data-id="element-832" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Liabilities and Equity Section */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-833">
          <h2 className="text-lg font-semibold mb-4" data-id="element-834">Pasivos y Capital</h2>
          <div className="mb-4" data-id="element-835">
            <div className="overflow-x-auto" data-id="element-836">
              <table className="w-full" data-id="element-837">
                <thead data-id="element-838">
                  <tr className="bg-gray-50" data-id="element-839">
                    <th className="py-2 px-4 text-left text-sm font-medium text-gray-500" data-id="element-840">
                      Concepto
                    </th>
                    <th className="py-2 px-4 text-right text-sm font-medium text-gray-500" data-id="element-841">
                      Valor
                    </th>
                    <th className="py-2 px-4 text-right text-sm font-medium text-gray-500" data-id="element-842">
                      %
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-843">
                  {balanceSheetData.liabilities.map(item => {
                  const percentage = item.value / (totalLiabilities + totalEquity) * 100;
                  return <tr key={item.name} data-id="element-844">
                        <td className="py-2 px-4 text-sm" data-id="element-845">{item.name}</td>
                        <td className="py-2 px-4 text-right text-sm" data-id="element-846">
                          {formatCurrency(item.value)}
                        </td>
                        <td className="py-2 px-4 text-right text-sm" data-id="element-847">
                          {percentage.toFixed(1)}%
                        </td>
                      </tr>;
                })}
                  <tr className="bg-orange-50 font-medium" data-id="element-848">
                    <td className="py-2 px-4 text-sm" data-id="element-849">Total Pasivos</td>
                    <td className="py-2 px-4 text-right text-sm" data-id="element-850">
                      {formatCurrency(totalLiabilities)}
                    </td>
                    <td className="py-2 px-4 text-right text-sm" data-id="element-851">
                      {(totalLiabilities / (totalLiabilities + totalEquity) * 100).toFixed(1)}
                      %
                    </td>
                  </tr>
                  {balanceSheetData.equity.map(item => {
                  const percentage = item.value / (totalLiabilities + totalEquity) * 100;
                  return <tr key={item.name} data-id="element-852">
                        <td className="py-2 px-4 text-sm" data-id="element-853">{item.name}</td>
                        <td className="py-2 px-4 text-right text-sm" data-id="element-854">
                          {formatCurrency(item.value)}
                        </td>
                        <td className="py-2 px-4 text-right text-sm" data-id="element-855">
                          {percentage.toFixed(1)}%
                        </td>
                      </tr>;
                })}
                  <tr className="bg-green-50 font-medium" data-id="element-856">
                    <td className="py-2 px-4 text-sm" data-id="element-857">Total Capital</td>
                    <td className="py-2 px-4 text-right text-sm" data-id="element-858">
                      {formatCurrency(totalEquity)}
                    </td>
                    <td className="py-2 px-4 text-right text-sm" data-id="element-859">
                      {(totalEquity / (totalLiabilities + totalEquity) * 100).toFixed(1)}
                      %
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="h-64" data-id="element-860">
            <ResponsiveContainer width="100%" height="100%" data-id="element-861">
              <PieChart data-id="element-862">
                <Pie data={[...balanceSheetData.liabilities, ...balanceSheetData.equity]} cx="50%" cy="50%" labelLine={false} label={({
                name,
                percent
              }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={80} fill="#8884d8" dataKey="value" data-id="element-863">
                  {balanceSheetData.liabilities.map((entry, index) => <Cell key={`liability-${index}`} fill={LIABILITY_COLORS[index % LIABILITY_COLORS.length]} data-id="element-864" />)}
                  {balanceSheetData.equity.map((entry, index) => <Cell key={`equity-${index}`} fill={EQUITY_COLORS[index % EQUITY_COLORS.length]} data-id="element-865" />)}
                </Pie>
                <Tooltip formatter={value => [formatCurrency(value as number), undefined]} data-id="element-866" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Financial Indicators */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-867">
        {balanceSheetData.indicators.map(indicator => <div key={indicator.name} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-868">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-869">
              {indicator.name}
            </h3>
            <p className="text-2xl font-bold mt-1 text-blue-600" data-id="element-870">
              {indicator.value.toFixed(1)}
            </p>
            <p className="mt-2 text-sm text-gray-500" data-id="element-871">
              {indicator.name === 'Razón circulante' && 'Activos circulantes / Pasivos a corto plazo'}
              {indicator.name === 'Prueba ácida' && '(Activos circulantes - Inventario) / Pasivos a corto plazo'}
              {indicator.name === 'Inventario en días' && 'Promedio de días que permanece el inventario'}
              {indicator.name === 'Cuentas por Cobrar (días)' && 'Promedio de días para cobrar a clientes'}
              {indicator.name === 'Cuentas por Pagar (días)' && 'Promedio de días para pagar a proveedores'}
            </p>
          </div>)}
      </div>
      {/* Working Capital Trend */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-872">
        <h2 className="text-lg font-semibold mb-4" data-id="element-873">
          Capital de Trabajo (Últimos 6 meses)
        </h2>
        <div className="h-72" data-id="element-874">
          <ResponsiveContainer width="100%" height="100%" data-id="element-875">
            <LineChart data={balanceSheetData.history} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-876">
              <CartesianGrid strokeDasharray="3 3" data-id="element-877" />
              <XAxis dataKey="month" data-id="element-878" />
              <YAxis tickFormatter={value => `$${(value / 1000).toFixed(0)}K`} data-id="element-879" />
              <Tooltip formatter={value => [formatCurrency(value as number), undefined]} labelFormatter={label => `Mes: ${label}`} data-id="element-880" />
              <Legend data-id="element-881" />
              <Line type="monotone" dataKey="workingCapital" name="Capital de Trabajo" stroke="#4F46E5" strokeWidth={2} data-id="element-882" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>;
};
export default BalanceSheet;